import { styled } from "goober";
import type { DisplayableSpace } from "../../hooks/useSpaceDisplayName";
import { SpaceBadge } from "../SpaceBadge";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";

const Cell = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Pill = styled("span")`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
`;

// The spaces a connector is available in — a derived, many-valued set (0, 1, or
// many spaces). Collapses to "All spaces" when it covers every shared space, and
// overflows past `max` badges into a "+N" pill.
export function ConnectorSpacesCell({
  spaces,
  sharedSpaceCount,
  displaySpaceName,
  max = 2,
}: {
  spaces: readonly DisplayableSpace[];
  sharedSpaceCount: number;
  displaySpaceName: (space: DisplayableSpace) => string;
  max?: number;
}) {
  if (spaces.length === 0) return <Text variant="text-small">—</Text>;

  if (sharedSpaceCount > 1 && spaces.length === sharedSpaceCount) {
    return (
      <Tooltip
        content={spaces.map((space) => displaySpaceName(space)).join(", ")}
      >
        <Pill>
          <Text variant="text-small-md">All spaces</Text>
        </Pill>
      </Tooltip>
    );
  }

  const shown = spaces.slice(0, max);
  const rest = spaces.slice(max);
  return (
    <Cell>
      {shown.map((space) => (
        <SpaceBadge key={space.id} space={space} />
      ))}
      {rest.length > 0 && (
        <Tooltip
          content={rest.map((space) => displaySpaceName(space)).join(", ")}
        >
          <Pill>
            <Text variant="text-small-md">{`+${rest.length}`}</Text>
          </Pill>
        </Tooltip>
      )}
    </Cell>
  );
}
